import React, { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Progress } from "../components/ui/progress";
import {
  User,
  FileText,
  Mail,
  Video,
  QrCode,
  LogOut,
  Menu,
  X,
  Bell,
  Settings,
  Inbox as InboxIcon,
  Handshake,
} from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { dashboardAPI, profileAPI } from "../services/api";
import ResumeBuilder from "../components/dashboard/ResumeBuilder";
import CoverLetterGenerator from "../components/dashboard/CoverLetterGenerator";
import QRCodePreview from "../components/dashboard/QRCodePreview";
import ProfileSection from "../components/dashboard/ProfileSection";
import IntroductionsSection from "../components/dashboard/IntroductionsSection";
import SuccessAgreementBuilder from "../components/dashboard/SuccessAgreementBuilder";
import InboxSection from "../components/dashboard/InboxSection";
import Logo from "../components/Logo";

const ApplicantDashboard = () => {
  const [activeSection, setActiveSection] = useState("profile");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inboxUnread, setInboxUnread] = useState(0);
  const [profile, setProfile] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAuthenticated, loading: authLoading } = useAuth();

  // Deep-link support: /dashboard/applicant?tab=inbox jumps directly to the
  // inbox tab (used by recruiter-action notification emails).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    if (tab && ["profile", "resume", "cover-letter", "video", "qr-preview", "inbox"].includes(tab)) {
      setActiveSection(tab);
    }
  }, [location.search]);

  useEffect(() => {
    // Wait for auth loading to complete
    if (authLoading) return;
    
    // Redirect to login if not authenticated
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }

    // Load dashboard stats + profile (for the canonical display name)
    loadDashboardStats();
    loadProfile();
  }, [isAuthenticated, authLoading, navigate]);

  const loadProfile = async () => {
    try {
      const p = await profileAPI.getProfile();
      setProfile(p);
    } catch (err) {
      // Non-fatal — fall back to user.first_name/last_name in the UI.
      console.warn("Could not load profile in dashboard:", err);
    }
  };

  const loadDashboardStats = async () => {
    try {
      const stats = await dashboardAPI.getStats();
      setDashboardStats(stats);
      if (typeof stats?.inbox_unread === "number") {
        setInboxUnread(stats.inbox_unread);
      }
    } catch (error) {
      console.error('Failed to load dashboard stats:', error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success("Logged out successfully!");
    navigate("/");
  };

  // Show loading state while auth is loading or dashboard data is loading
  if (authLoading || loading || !user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">
            {authLoading ? "Checking authentication..." : "Loading dashboard..."}
          </p>
        </div>
      </div>
    );
  }

  // Iter-26.1: prefer the canonical profile name so a freshly-signed-up
  // "Test User" account or a user who edited their Profile shows the
  // real candidate identity everywhere in the dashboard. Falls back to
  // first_name + last_name from the auth user only if profile is empty.
  const displayName =
    (profile?.full_name && profile.full_name.trim())
    || (user ? `${user.first_name || ""} ${user.last_name || ""}`.trim() : "")
    || "User";
  const userEmail = (profile?.email && profile.email.trim()) || user?.email || '';
  
  // Get dashboard data
  const profileCompletion = dashboardStats?.profile_completion || 0;
  const resumeStatus = dashboardStats?.resume_status || "Not Started";
  const coverLetterStatus = dashboardStats?.cover_letter_status || "Not Started"; 
  const videoStatus = dashboardStats?.video_status || "Not Started";

  const sidebarItems = [
    { id: "profile", label: "Profile", icon: User },
    { id: "resume", label: "Resume Builder", icon: FileText },
    { id: "cover-letter", label: "Cover Letter", icon: Mail },
    { id: "video", label: "Video Interview", icon: Video },
    { id: "success-agreement", label: "Success Agreement", icon: Handshake },
    { id: "qr-preview", label: "QR & Public Profile", icon: QrCode },
    { id: "inbox", label: "Inbox", icon: InboxIcon, badge: inboxUnread },
  ];

  const renderContent = () => {
    switch (activeSection) {
      case "profile":
        return <ProfileSection user={user} onProfileUpdate={() => { loadDashboardStats(); loadProfile(); }} />;
      case "resume":
        return <ResumeBuilder />;
      case "cover-letter":
        return <CoverLetterGenerator />;
      case "video":
        return <IntroductionsSection />;
      case "success-agreement":
        return <SuccessAgreementBuilder />;
      case "qr-preview":
        return <QRCodePreview />;
      case "inbox":
        return <InboxSection onCountChange={setInboxUnread} />;
      default:
        return <ProfileSection user={user} onProfileUpdate={() => { loadDashboardStats(); loadProfile(); }} />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white/95 backdrop-blur-md border-b border-blue-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo and Mobile Menu */}
            <div className="flex items-center">
              <button
                className="lg:hidden p-2 rounded-md text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-colors mr-2"
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              >
                {isSidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
              <Link to="/" className="hover:opacity-90 transition-opacity" aria-label="Inter-View home">
                <Logo height={28} priority />
              </Link>
            </div>

            {/* User Menu */}
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="sm" className="hidden sm:flex">
                <Bell className="h-4 w-4 mr-2" />
                Notifications
              </Button>
              <Button variant="ghost" size="sm" className="hidden sm:flex">
                <Settings className="h-4 w-4 mr-2" />
                Settings
              </Button>
              <div className="flex items-center space-x-3">
                <Avatar className="h-8 w-8">
                  <AvatarImage src="" />
                  <AvatarFallback className="bg-blue-100 text-blue-600">
                    {displayName.split(' ').map(n => n[0]).join('')}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden sm:block">
                  <p className="text-sm font-medium text-gray-900">{displayName}</p>
                  <p className="text-xs text-gray-500">Job Seeker</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white border-r border-blue-100 transition-transform duration-300 ease-in-out lg:transition-none`}>
          <div className="flex flex-col h-full pt-16 lg:pt-6">
            {/* Profile Summary */}
            <div className="px-6 pb-6 border-b border-blue-100">
              <div className="flex items-center space-x-3 mb-4">
                <Avatar className="h-12 w-12">
                  <AvatarImage src="" />
                  <AvatarFallback className="bg-blue-100 text-blue-600">
                    {displayName.split(' ').map(n => n[0]).join('')}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium text-gray-900">{displayName}</p>
                  <p className="text-sm text-gray-500">Job Seeker</p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Profile Completion</span>
                  <span className="font-medium text-blue-600">{profileCompletion}%</span>
                </div>
                <Progress value={profileCompletion} className="h-2" />
              </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 px-6 py-6">
              <ul className="space-y-2">
                {sidebarItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <li key={item.id}>
                      <button
                        onClick={() => {
                          setActiveSection(item.id);
                          setIsSidebarOpen(false);
                        }}
                        data-testid={`sidebar-${item.id}-btn`}
                        className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                          activeSection === item.id
                            ? 'bg-blue-100 text-blue-600'
                            : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50'
                        }`}
                      >
                        <Icon className="h-5 w-5 mr-3" />
                        <span className="flex-1 text-left">{item.label}</span>
                        {item.badge > 0 && (
                          <span
                            className="ml-2 inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full bg-blue-600 text-white text-[10px] font-bold"
                            data-testid={`sidebar-${item.id}-badge`}
                          >
                            {item.badge > 99 ? "99+" : item.badge}
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>

            {/* Logout */}
            <div className="px-6 py-6 border-t border-blue-100">
              <Button
                onClick={handleLogout}
                variant="ghost"
                className="w-full justify-start text-gray-700 hover:text-red-600 hover:bg-red-50"
              >
                <LogOut className="h-5 w-5 mr-3" />
                Logout
              </Button>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 lg:ml-0">
          <div className="p-6 lg:p-8">
            {renderContent()}
          </div>
        </main>
      </div>

      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default ApplicantDashboard;